import React, { Component } from 'react';
import PropTypes from 'prop-types';
import RenderIf from '../../utils/JSXUtils';

const propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  placeholder: PropTypes.string,
  className: PropTypes.string,
  password: PropTypes.bool,
  inline: PropTypes.bool,
  disabled: PropTypes.bool,
  required: PropTypes.bool,
  readOnly: PropTypes.bool,
  selectAllOnFocus: PropTypes.bool,
  validator: PropTypes.func,
  isValid: PropTypes.bool,
  error: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  showError: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
  iconLeft: PropTypes.func, // Icon.*
  iconRight: PropTypes.func, // Icon.*
  onChange: PropTypes.func,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  onKeyDown: PropTypes.func,
  onKeyUp: PropTypes.func,
  onKeyPress: PropTypes.func
};

const defaultProps = {
  className: '',
  placeholder: '',
  password: false,
  inline: false,
  disabled: false,
  required: false,
  readOnly: false,
  selectAllOnFocus: false,
  validator: () => false,
  isValid: true,
  error: false,
  showError: ({ blurred }) => blurred
};

export default class TextInput extends Component {
  constructor(props) {
    super(props);

    const { error, isValid } = this.validate(this.props.value);
    const events = { focused: false, changed: false, blurred: false };
    const showError = this.showError(this.props.showError, events);

    this.state = { ...events, showError, isValid, error, value: props.value };

    this.onFocus = this.onFocus.bind(this);
    this.onChange = this.onChange.bind(this);
    this.onBlur = this.onBlur.bind(this);
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (nextProps.isValid !== this.props.isValid) {
      this.setState((prevState) => ({ ...prevState, isValid: nextProps.isValid, error: nextProps.error, value:
        nextProps.value }));
    }
    else if (nextProps.value !== this.state.value) {
      this.setState({ value: nextProps.value });
    }
  }

  onFocus(event) {
    const { selectAllOnFocus } = this.props;
    const { value } = event.target;
    const { error, isValid } = this.validate(value);
    const focused = true;
    const showError = this.showError(this.props.showError, { focused });

    if (selectAllOnFocus) {
      event.target.select();
    }

    this.setState((prevState) => ({ ...prevState, focused, showError, isValid, error, value }), () => {
      if (this.props.onFocus) {
        this.props.onFocus(value, isValid, error);
      }
    });
  }

  onChange(event) {
    const { value } = event.target;
    const { error, isValid } = this.validate(value);
    const changed = true;
    const showError = this.showError(this.props.showError, { changed });

    this.setState((prevState) => ({ ...prevState, changed, showError, isValid, error, value }), () => {
      if (this.props.onChange) {
        this.props.onChange(value, isValid, error);
      }
    });
  }

  onBlur(event) {
    const { value } = event.target;
    const { error, isValid } = this.validate(value);
    const blurred = true;
    const showError = this.showError(this.props.showError, { blurred });

    this.setState((prevState) => ({ ...prevState, blurred, showError, isValid, error, value }), () => {
      if (this.props.onBlur) {
        this.props.onBlur(value, isValid, error);
      }
    });
  }

  validate(value) {
    const error = this.props.validator(value);
    const isValid = !error;

    return { error, isValid };
  }

  showError(showError, events = {}) {
    const { focused, changed, blurred } = Object.assign({}, this.state, events);

    if (typeof showError === 'function') {
      return showError({ focused, changed, blurred });
    }

    return showError;
  }

  render() {
    const { id, label, inline, password, placeholder, className, required, readOnly, disabled } = this.props;
    const { onKeyDown, onKeyUp, onKeyPress } = this.props;
    const { iconLeft: IconLeft, iconRight: IconRight } = this.props;
    const { showError, isValid, error, value } = this.state;
    const inputType = password ? 'password' : 'text';
    const wrapperClass = inline ? 'rec-form-inline-item' : 'rec-form-item-wrap';
    const classes = `${wrapperClass} ${className} ${IconLeft ? 'with-left-icon' : ''} ${IconRight ? 'with-right-icon' : ''}`;

    return (
      <div className={classes}>
        {RenderIf(label)(<label htmlFor={id}>{label}{required ? <mark title="required">*</mark> : null}</label>)}
        {IconLeft ? <IconLeft /> : null}
        <input
          id={id}
          value={value}
          placeholder={placeholder}
          readOnly={readOnly}
          disabled={disabled}
          className={(showError && !isValid) ? 'rec-error' : ''}
          type={inputType}
          onFocus={this.onFocus}
          onChange={this.onChange}
          onBlur={this.onBlur}
          onKeyDown={onKeyDown}
          onKeyUp={onKeyUp}
          onKeyPress={onKeyPress} />
        {IconRight ? <IconRight /> : null}
        {RenderIf(showError && !isValid)(<div>{error}</div>)}
      </div>
    );
  }
}

TextInput.propTypes = propTypes;
TextInput.defaultProps = defaultProps;
